import useAxios from "./../../../Hooks/useAxios";
import { useEffect, useState } from "react";

// Icons
import {
  FiUser,
  FiCalendar,
  FiActivity,
  FiThermometer,
  FiTrendingUp,
  FiHeart,
  FiTarget,
  FiWind,
  FiDroplet,
  FiArrowUp,
  FiArrowDown,
  FiFileText,
} from "react-icons/fi";

function Vitals({ visits = [] }) {
  const [vitalsByVisit, setVitalsByVisit] = useState({});
  const request = useAxios();

  const safe = (value) => value ?? "—";

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getVitals = async () => {
    if (!visits?.length) return;
    const visitIds = visits.map((v) => v?.id).filter(Boolean);
    if (!visitIds.length) return;

    const res = await request({
      method: "GET",
      url: "chapchap/list/visit_vitals",
      params: { "visit_id-int": visitIds.join(",") },
      showSuccessModal: false,
      showLoadingModal: false,
    });

    if (!res?._error && res?.data?.docs?.length) {
      // Group vitals by visit ID
      const grouped = res.data.docs.reduce((acc, vitals) => {
        const vid = vitals?.visits?.id ?? "unknown";
        if (!acc[vid]) acc[vid] = [];
        acc[vid].push(vitals);
        return acc;
      }, {});
      setVitalsByVisit(grouped);
    }
  };

  useEffect(() => {
    getVitals();
  }, [visits]);

  if (!Object.keys(vitalsByVisit).length) {
    return (
      <div className="p-5 text-center text-auro_metal_saurus">
        No vitals recorded yet.
      </div>
    );
  }

  // Map each vital to its icon
  const vitalIcons = {
    Height: <FiTrendingUp />,
    Weight: <FiTarget />,
    Temperature: <FiThermometer />,
    "Blood Pressure": <FiHeart />,
    "Systolic BP": <FiArrowUp />,
    "Diastolic BP": <FiArrowDown />,
    "Respiratory Rate": <FiWind />,
    "Pulse Rate": <FiActivity />,
    "Blood Sugar": <FiDroplet />,
    SpO2: <FiDroplet />,
    BMI: <FiTrendingUp />,
    Comments: <FiFileText />,
  };

  return (
    <div className="w-full p-5 flex flex-col gap-8">
      {/* MAIN SECTION HEADING */}
      <h2 className="text-3xl font-bold text-yankees_blue text-center">
        Patient Vitals History
      </h2>

      {Object.entries(vitalsByVisit).map(([visitId, vitalsList]) => {
        const vitals = vitalsList[0];
        const visit = vitals?.visits;
        const recordedBy = vitals?.team?.name;

        const fields = {
          Height: vitals?.height,
          Weight: vitals?.weight,
          Temperature: vitals?.temparature,
          "Blood Pressure": vitals?.blood_preasure,
          "Systolic BP": vitals?.systolic_bp,
          "Diastolic BP": vitals?.diastolic_bp,
          "Respiratory Rate": vitals?.respiratory_rate,
          "Pulse Rate": vitals?.pulse_rate,
          "Blood Sugar": vitals?.blood_sugar,
          SpO2: vitals?.sp02,
          BMI: vitals?.bmi,
          Comments: vitals?.comments,
        };

        const visibleFields = Object.entries(fields).filter(
          ([, value]) => value !== null && value !== ""
        );

        if (!visibleFields.length) return null;

        return (
          <div
            key={visitId}
            className="bg-white shadow-xl rounded-3xl p-8 border border-bright_grey w-full"
          >
            {/* Header */}
            <div className="flex items-center gap-4 pb-5 border-b border-bright_grey mb-6">
              <div className="w-16 h-16 rounded-full bg-ghost_white flex items-center justify-center text-3xl text-primary font-bold shadow-inner">
                {recordedBy?.[0] || "?"}
              </div>

              <div>
                <h2 className="text-2xl font-bold text-yankees_blue">
                  {safe(recordedBy)}
                </h2>
              </div>

              <div className="ml-auto text-auro_metal_saurus text-sm">
                Visit Date: {formatDate(visit?.created_at)}
              </div>
            </div>

            {/* *** VITALS ALL TOGETHER INSIDE ONE CARD *** */}
            <div>
              <h3 className="text-xl font-semibold text-yankees_blue mb-4">
                Vitals Recorded
              </h3>

              {/* Grid of vitals */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
                {visibleFields.map(([label, value]) => (
                  <VitalCard
                    key={label}
                    label={label}
                    value={safe(value)}
                    icon={vitalIcons[label] || <FiActivity />}
                  />
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-bright_grey mt-4"></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const VitalCard = ({ label, value, icon }) => (
  <div className="flex flex-col justify-between p-4 bg-white rounded-xl shadow border border-bright_grey hover:shadow-md transition">
    <div className="flex items-center gap-2 text-auro_metal_saurus mb-2">
      <span className="text-primary text-xl">{icon}</span>
      <span className="font-medium">{label}</span>
    </div>
    <div className="text-yankees_blue font-semibold text-lg">{value}</div>
  </div>
);

export default Vitals;
